/**
 * Ride Tracking Page
 * Shows ride status, driver info, and trip progress
 */
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  ArrowLeft, MapPin, Navigation, Car, User, Phone, Star,
  Clock, CheckCircle, Circle, Loader2, MessageCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RIDE_STAGES = [
  { key: 'requested', label: 'Ride Requested', icon: Circle },
  { key: 'accepted', label: 'Driver Assigned', icon: Car },
  { key: 'arrived', label: 'Driver Arrived', icon: MapPin },
  { key: 'in_progress', label: 'Trip In Progress', icon: Navigation },
  { key: 'completed', label: 'Trip Completed', icon: CheckCircle }
];

const RideTracking = () => {
  const navigate = useNavigate();
  const { rideId } = useParams();
  const { token } = useAuth();
  
  const [ride, setRide] = useState(null);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (rideId) {
      fetchRideDetails();
      // Poll for updates every 10 seconds
      const interval = setInterval(fetchRideDetails, 10000);
      return () => clearInterval(interval);
    }
  }, [rideId]);

  const fetchRideDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/rides/${rideId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();

      if (data.success) {
        setRide(data.ride);
        if (data.ride.driver_id) {
          fetchDriverDetails(data.ride.driver_id);
        }
      } else {
        setError(data.error?.message || 'Ride not found');
      }
    } catch (err) {
      setError('Failed to load ride details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverDetails = async (driverId) => {
    try {
      const response = await fetch(`${API_URL}/api/drivers/${driverId}/public`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success || data.driver) {
        setDriver(data.driver || data);
      }
    } catch (err) {
      console.error('Failed to fetch driver:', err);
    }
  };

  const getCurrentStageIndex = () => {
    if (!ride) return -1;
    const status = ride.status;
    
    if (status === 'completed') return 4;
    if (status === 'in_progress') return 3;
    if (status === 'arrived' || ride.driver_arrived) return 2;
    if (status === 'accepted' || ride.driver_id) return 1;
    return 0;
  };

  const handleCancelRide = async () => {
    if (!window.confirm('Are you sure you want to cancel this ride?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/rides/${rideId}/cancel`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      if (data.success) {
        navigate('/dashboard');
      } else {
        alert(data.error?.message || 'Failed to cancel ride');
      }
    } catch (err) {
      alert('Failed to cancel ride');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading ride details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate('/dashboard')}>
            Return to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const currentStage = getCurrentStageIndex();
  const isScheduled = ride?.ride_type === 'airport_transfer' || ride?.scheduled_time;
  const isCompleted = ride?.status === 'completed';
  const isCancelled = ride?.status === 'cancelled';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <div className="bg-emerald-600 text-white p-4">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-emerald-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold">Track Your Ride</h1>
            <p className="text-emerald-100 text-sm">ID: {rideId}</p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4 max-w-lg mx-auto">
        {/* Status Badge */}
        <div className={`text-center py-3 rounded-xl font-semibold ${
          isCompleted ? 'bg-green-100 text-green-700' :
          isCancelled ? 'bg-red-100 text-red-700' :
          isScheduled ? 'bg-blue-100 text-blue-700' :
          'bg-amber-100 text-amber-700'
        }`}>
          {isCompleted ? '✓ Trip Completed' :
           isCancelled ? '✕ Ride Cancelled' :
           isScheduled ? `📅 Scheduled: ${new Date(ride.scheduled_time).toLocaleString('en-JM', {
             month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
           })}` :
           '🚗 Ride In Progress'}
        </div>

        {/* Progress Timeline */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Trip Progress</h3>
          <div className="space-y-4">
            {RIDE_STAGES.map((stage, idx) => {
              const isActive = idx === currentStage;
              const isComplete = idx < currentStage;
              const Icon = stage.icon;
              
              return (
                <div key={stage.key} className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    isComplete ? 'bg-emerald-500 text-white' :
                    isActive ? 'bg-emerald-100 text-emerald-600 ring-2 ring-emerald-500' :
                    'bg-gray-100 text-gray-400'
                  }`}>
                    {isComplete ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : isActive ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      isComplete || isActive ? 'text-gray-800' : 'text-gray-400'
                    }`}>
                      {stage.label}
                    </p>
                  </div>
                  {isComplete && (
                    <CheckCircle className="w-5 h-5 text-emerald-500" />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Driver Info (if assigned) */}
        {driver && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Your Driver
            </h3>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                <User className="w-8 h-8 text-emerald-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{driver.user_name || 'Driver'}</p>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  <span>{driver.rating?.toFixed(1) || '5.0'}</span>
                  <span>•</span>
                  <span>{driver.completed_rides || 0} rides</span>
                </div>
                {driver.vehicle_type && (
                  <p className="text-sm text-gray-500 mt-1">
                    {driver.vehicle_type} • {driver.license_plate}
                  </p>
                )}
              </div>
            </div>
            {driver.user_phone && !isCompleted && (
              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => window.open(`tel:${driver.user_phone}`)}
                  variant="outline"
                  className="flex-1"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Driver
                </Button>
                <Button
                  variant="outline"
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Message
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Trip Details */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
          <h3 className="font-semibold text-gray-800">Trip Details</h3>
          
          <div className="flex items-start gap-3">
            <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1.5" />
            <div>
              <p className="text-xs text-gray-500">Pickup</p>
              <p className="font-medium text-gray-800">
                {ride?.pickup?.address || ride?.pickup_address || 'Loading...'}
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-3">
            <div className="w-3 h-3 bg-red-500 rounded-full mt-1.5" />
            <div>
              <p className="text-xs text-gray-500">Destination</p>
              <p className="font-medium text-gray-800">
                {ride?.dropoff?.address || ride?.dropoff_address || ride?.destination_name || 'Loading...'}
              </p>
            </div>
          </div>

          {ride?.estimated_fare && (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Fare</span>
                <span className="text-xl font-bold text-emerald-600">
                  {ride.currency === 'USD' 
                    ? `$${ride.flat_rate_usd || ride.estimated_fare} USD`
                    : `J$${ride.estimated_fare?.toLocaleString()}`
                  }
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Cancel Button (if ride can be cancelled) */}
        {!isCompleted && !isCancelled && currentStage < 3 && (
          <Button
            onClick={handleCancelRide}
            variant="outline"
            className="w-full border-red-300 text-red-600 hover:bg-red-50"
          >
            Cancel Ride
          </Button>
        )}

        {/* Completed Actions */}
        {isCompleted && (
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/ride/book')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              Book Another Ride
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              variant="outline"
              className="w-full"
            >
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RideTracking;
